import React, { useMemo } from "react";
import {
  Box,
  HStack,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { CircleAlert } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import type { Backtest } from "../../../types/backtests";
import type { Report } from "../../../types/reports";
import type { Trade } from "../../../types/trade";
import TradeReviewTab, {
  type TradeReviewTabProps,
  type TradeReviewSymbolDiscovery,
} from "../TradeLog/components/TradeReview/TradeReviewTab";
import {
  TRADE_REVIEW_ALERT_CLASS,
  TRADE_REVIEW_ALERT_DESC_CLASS,
} from "../TradeLog/components/TradeReview/tradeReviewAlertUi";
import { FieldSelectFit } from "@/components/trade-filter-selects";
import { InstrumentSetupGate } from "../../../components/Instruments/InstrumentSetupGate";
import { useReportsDetailColors } from "./reportsDetailTheme";
import { resolveReportTradeReviewRequestWindow, EXECUTION_CANDLES_TRADE_IDS_SOFT_CAP } from "../../../utils/executionCandlesParams";
import { formatDateAsCalendarDayInTimeZone } from "../../../utils/notificationPeriod";
import { resolveReportTradeReviewRequestLocalWindow } from "../../../utils/reportTradeReviewWindow";
import { filterTradesExitedOnOrBefore } from "../../../utils/reportTradingPnlSeries";
import type { CohortListFilter, CohortPreviewStats } from "../../../utils/cohortTradesResolve";

export type ReportTradeReviewPanelProps = {
  brokerAccountCode: string | null;
  trades: Trade[];
  tradesLoading: boolean;
  tradesError: string | null;
  reportId: string;
  /** Real report UUID for execution-candles cohort — omit for standalone simulator runs. */
  reportIdForCohort?: string;
  symbolDiscovery?: TradeReviewSymbolDiscovery;
  cohortIntent?: TradeReviewTabProps["cohortIntent"];
  reportTimeframe?: string | null;
  reportStartDate?: string | null;
  reportEndDate?: string | null;
  /** IANA zone for cohort local dates (not the formatted offset label). */
  reportTimezone?: string | null;
  cohortPeriod?: Report["cohort_period"];
  inputSnapshot?: unknown;
  /** Overrides default report all-time window (e.g. standalone simulator scope). */
  chartRequestWindow?: TradeReviewTabProps["requestWindow"];
  /**
   * Inclusive as-of instant (report `created_at`). Freezes all-time / open-ended
   * windows and drops trades closed after report generation.
   */
  asOfEndMs?: number | null;
  defaultCandleTimeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
  recordedTimezoneLabel?: string | null;
  linkedBacktests: Backtest[];
  overlayBacktestId: string;
  onOverlayBacktestIdChange: (id: string) => void;
  /** When true, Trade Review is blocked until instrument setup completes. */
  requireInstrumentSetup?: boolean;
  overlayEnabled: boolean;
  onOverlayEnabledChange: (enabled: boolean) => void;
  /** @deprecated Unused — overlay loads silently. Kept optional for call-site compatibility. */
  overlayTradeIdsLoading?: boolean;
  overlayTradeIdsError: string | null;
  overlayTradeIds: { kept: string[]; removed: string[] } | null;
  policyOverlay: { kept_trade_ids: string[]; removed_trade_ids: string[] } | null;
  backtestId?: string | null;
  simulationOverlayMode?: string;
  backtestSimulationRows?: Array<Record<string, unknown>>;
  /** Exit Placement: legs replayed for chart SL/TP gating. */
  simulatedLegs?: Array<"sl" | "tp">;
  /** When true, show section title chrome (trade-analysis overview embed). */
  showSectionHeader?: boolean;
  /** Optional override for the section description under the title. */
  sectionDescription?: string;
  /** When set (e.g. simulator results page), overrides report-detail muted text color. */
  subtleText?: string;
  /** Session-persist chart UI for this panel slot (report detail tab switches). */
  uiPersistenceKey?: string;
  cohortHighlightIds?: string[];
  /** Exact trade IDs for cohort preview chart scoping (mutually exclusive with reportIdForCohort). */
  exactTradeIds?: string[];
  /** Demo Entry Analysis job id for preview candles / resolve (poll cookie). */
  demoJobId?: string;
  /** Seed chart symbol when demo has no local trade rows yet. */
  forceChartSymbol?: string;
  cohortPreviewMode?: boolean;
  cohortListFilter?: CohortListFilter;
  onCohortPreviewStats?: (stats: CohortPreviewStats | null) => void;
  hideOverlayControls?: boolean;
};

function clampTradeReviewWindowToAsOf(
  window: NonNullable<TradeReviewTabProps["requestWindow"]>,
  asOfEndMs: number | null | undefined,
  fallbackTimezone?: string | null,
  cohortPeriod?: Report["cohort_period"],
  reportTimeframe?: string | null,
  reportStartDate?: string | null,
  reportEndDate?: string | null
): NonNullable<TradeReviewTabProps["requestWindow"]> {
  if (asOfEndMs == null || !Number.isFinite(asOfEndMs)) return window;
  const zone =
    window.timezone?.trim() ||
    fallbackTimezone?.trim() ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  const asOfYmd = formatDateAsCalendarDayInTimeZone(new Date(asOfEndMs), zone);

  const parseYmd = (raw: string | null | undefined): Date | null => {
    if (!raw || typeof raw !== "string") return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw.trim());
    if (!m) return null;
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const d = parseInt(m[3], 10);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
    return new Date(y, mo - 1, d, 0, 0, 0, 0);
  };

  // Prefer the same cohort resolver used for fetch (365-day all-time / cohort_period).
  if (
    window.timeframe === "all-time" ||
    (!window.local_start_date && !window.start_date) ||
    cohortPeriod?.local_start_date
  ) {
    return resolveReportTradeReviewRequestLocalWindow({
      dateRange: {
        startDate: parseYmd(reportStartDate),
        endDate: parseYmd(reportEndDate),
      },
      timeframe: reportTimeframe ?? window.timeframe ?? "all-time",
      reportingTimeZone: zone,
      asOfYmd,
      cohortPeriod,
    });
  }

  if (window.local_start_date && window.local_end_date) {
    const end =
      window.local_end_date > asOfYmd ? asOfYmd : window.local_end_date;
    return {
      local_start_date: window.local_start_date,
      local_end_date: end,
      timezone: zone,
    };
  }

  return resolveReportTradeReviewRequestLocalWindow({
    dateRange: {
      startDate: parseYmd(reportStartDate),
      endDate: parseYmd(reportEndDate),
    },
    timeframe: reportTimeframe ?? window.timeframe ?? "all-time",
    reportingTimeZone: zone,
    asOfYmd,
    cohortPeriod,
  });
}

function ReportTradeReviewPanelInner(props: ReportTradeReviewPanelProps): React.ReactElement {
  const reportsDetailColors = useReportsDetailColors();
  const subtleText = props.subtleText ?? reportsDetailColors.subtleText;

  const overlayMenuOptions = useMemo(
    () => [
      { value: "", label: "No overlay" },
      ...props.linkedBacktests.map((b) => ({
        value: b.id,
        label: b.title || `Simulator ${b.id.slice(0, 8)}…`,
      })),
    ],
    [props.linkedBacktests]
  );

  const asOfEndMs =
    props.asOfEndMs != null && Number.isFinite(props.asOfEndMs) ? props.asOfEndMs : null;

  const resolvedRequestWindow = useMemo(() => {
    const base =
      props.chartRequestWindow ??
      resolveReportTradeReviewRequestWindow(
        {
          timeframe: props.reportTimeframe,
          start_date: props.reportStartDate,
          end_date: props.reportEndDate,
          timezone: props.reportTimezone,
          cohort_period: props.cohortPeriod,
          input_snapshot: props.inputSnapshot,
        },
        props.reportTimezone
      );
    return clampTradeReviewWindowToAsOf(
      base,
      asOfEndMs,
      props.reportTimezone,
      props.cohortPeriod,
      props.reportTimeframe,
      props.reportStartDate,
      props.reportEndDate
    );
  }, [
    asOfEndMs,
    props.chartRequestWindow,
    props.cohortPeriod,
    props.inputSnapshot,
    props.reportEndDate,
    props.reportStartDate,
    props.reportTimeframe,
    props.reportTimezone,
  ]);

  const tradesForReview = useMemo(
    () => filterTradesExitedOnOrBefore(props.trades, asOfEndMs),
    [asOfEndMs, props.trades]
  );

  const hasExactTradeIds =
    (props.exactTradeIds?.length ?? 0) > 0 &&
    (props.exactTradeIds?.length ?? 0) <= EXECUTION_CANDLES_TRADE_IDS_SOFT_CAP;

  const cohortIntent: TradeReviewTabProps["cohortIntent"] = hasExactTradeIds
    ? "policy_exact"
    : props.cohortIntent ?? "report";

  // `reportIdForCohort={undefined}` is indistinguishable from omitting the prop, so
  // report-detail falls back to `reportId`. Backtest / simulator intent must never
  // inherit that fallback — a linked but missing report 404s candles.
  const reportIdForCohort =
    hasExactTradeIds
      ? undefined
      : cohortIntent === "backtest"
        ? props.reportIdForCohort?.trim() || undefined
        : props.reportIdForCohort !== undefined
          ? props.reportIdForCohort?.trim() || undefined
          : props.reportId?.trim() || undefined;

  const tradeReviewProps: TradeReviewTabProps = {
    brokerAccountCode: props.brokerAccountCode,
    tradesForSymbols: tradesForReview,
    symbolDiscovery:
      props.symbolDiscovery ?? ({ kind: "report", id: props.reportId } as TradeReviewSymbolDiscovery),
    // Exact trade_ids and report_id are mutually exclusive for execution-candles.
    // Large exact lists must not clear report_id — BE resolves the cohort from report_id.
    reportIdForCohort,
    cohortIntent,
    defaultCandleTimeframe: props.defaultCandleTimeframe,
    showEditColumn: false,
    visualStyle: "reports",
    suppressHeader: true,
    requestWindow: resolvedRequestWindow,
    asOfEndMs,
    policyOverlay: props.policyOverlay ?? undefined,
    recordedTimezoneLabel: props.recordedTimezoneLabel ?? undefined,
    backtestId: props.overlayEnabled ? props.backtestId ?? undefined : undefined,
    simulationOverlayMode: props.simulationOverlayMode,
    backtestSimulationRows: props.backtestSimulationRows,
    simulatedLegs: props.simulatedLegs,
    collapsibleTradesList: true,
    uiPersistenceKey: props.uiPersistenceKey,
    cohortHighlightIds: props.cohortHighlightIds,
    exactTradeIds: props.exactTradeIds,
    demoJobId: props.demoJobId,
    forceChartSymbol: props.forceChartSymbol,
    cohortPreviewMode: props.cohortPreviewMode,
    cohortListFilter: props.cohortListFilter,
    onCohortPreviewStats: props.onCohortPreviewStats,
    beforeCandleTypeControls: props.hideOverlayControls ? undefined : (
      <FieldSelectFit
        id="trade-review-simulation-overlay"
        filterChrome="reports"
        value={props.overlayBacktestId}
        onValueChange={(id) => {
          props.onOverlayBacktestIdChange(id);
          props.onOverlayEnabledChange(Boolean(id.trim()));
        }}
        options={overlayMenuOptions}
        disabled={props.linkedBacktests.length === 0}
        placeholder="Select simulation overlay"
        emptyValueShowsPlaceholder
      />
    ),
  };

  return (
    <Box w="100%" data-reports-trade-review-section="">
      {props.showSectionHeader ? (
        <Box mb={4}>
          <Text
            fontSize={{ base: "2xl", md: "3xl" }}
            color="#38BBD1"
            fontWeight="normal"
            fontFamily="var(--font-primary)"
            lineHeight="1.15"
          >
            Trade review
          </Text>
          <Text fontSize="sm" color={subtleText} mt={1} fontFamily="var(--font-primary)">
            {props.sectionDescription ??
              "Chart and trade list for closed trades that exited in this report window. Link a saved simulation to highlight kept vs removed trades."}
          </Text>
        </Box>
      ) : null}

      {props.overlayBacktestId && props.overlayTradeIdsError ? (
        <Alert variant="error" className={TRADE_REVIEW_ALERT_CLASS}>
          <CircleAlert aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            {props.overlayTradeIdsError}
          </AlertDescription>
        </Alert>
      ) : null}

      {props.tradesLoading ? (
        <HStack spacing={2} mb="16px">
          <Spinner size="sm" />
          <Text color={subtleText} fontSize="sm" fontFamily="'Urbanist', sans-serif">
            Loading trades for review…
          </Text>
        </HStack>
      ) : null}
      {props.tradesError ? (
        <Alert variant="error" className={TRADE_REVIEW_ALERT_CLASS}>
          <CircleAlert aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            {props.tradesError}
          </AlertDescription>
        </Alert>
      ) : null}

      <InstrumentSetupGate featureLabel="Trade Review" bypass={!props.requireInstrumentSetup}>
        <TradeReviewTab {...tradeReviewProps} />
      </InstrumentSetupGate>
    </Box>
  );
}

export const ReportTradeReviewPanel = React.memo(ReportTradeReviewPanelInner);
